// packages/web-ui/src/api/clients/sessions.ts
//
// W2-S8 — Typed client for the chat-sessions REST surface (Arch §3.2 lines
// 230–233 + W1-S5 schemas + W1-S9 server-side `SessionStore`).
//
// Endpoints:
//   GET    /api/v1/chat/sessions               → SessionSummary[]
//   POST   /api/v1/chat/sessions               → { sessionId, createdAt }
//   GET    /api/v1/chat/sessions/:sessionId    → ChatTranscript
//   DELETE /api/v1/chat/sessions/:sessionId    → { deleted: true }    (HARD DELETE — RB-locked answer #5)
//
// The client returns `Result<T, ApiError>` per `apiCall` envelope. No throws.

import type { Result } from '@tessa-lean/base';
import type {
  ChatSessionsDeleteAllResponse,
  ChatSessionsListResponse,
  ChatTranscript,
  ClearMessagesResponse,
  CreateSessionRequest,
  CreateSessionResponse,
  DeleteMessageRequest,
  DeleteMessageResponse,
  DeleteSessionResponse,
} from '@tessa-lean/base/api-schemas/index.js';
import { apiCall, type ApiContext, type ApiError } from '../client.js';

/** GET /api/v1/chat/sessions — list summaries (sorted server-side by updatedAt desc). */
export async function listSessions(
  ctx: ApiContext,
): Promise<Result<ChatSessionsListResponse, ApiError>> {
  return apiCall<undefined, ChatSessionsListResponse>(ctx, 'GET', `/api/v1/chat/sessions`);
}

/** POST /api/v1/chat/sessions — create a new empty session. */
export async function createSession(
  ctx: ApiContext,
  body: CreateSessionRequest = {},
): Promise<Result<CreateSessionResponse, ApiError>> {
  return apiCall<CreateSessionRequest, CreateSessionResponse>(
    ctx,
    'POST',
    `/api/v1/chat/sessions`,
    body,
  );
}

/** GET /api/v1/chat/sessions/:sessionId — full transcript. */
export async function getTranscript(
  ctx: ApiContext,
  sessionId: string,
): Promise<Result<ChatTranscript, ApiError>> {
  return apiCall<undefined, ChatTranscript>(
    ctx,
    'GET',
    `/api/v1/chat/sessions/${encodeURIComponent(sessionId)}`,
  );
}

/**
 * DELETE /api/v1/chat/sessions/:sessionId — HARD DELETE.
 *
 * RB-locked answer #5 (UX §14 line 2158): the JSON file at
 * `<workspaceRoot>/.tessa-lean/sessions/<sessionId>.json` is unlinked from
 * disk by W1-S9's `SessionStore.delete()`. There is no soft-archive.
 *
 * Idempotent: the route returns 200 `{deleted: true}` whether or not the
 * file existed (W1-S9 AC #6 implementation note 1).
 */
export async function deleteSession(
  ctx: ApiContext,
  sessionId: string,
): Promise<Result<DeleteSessionResponse, ApiError>> {
  return apiCall<undefined, DeleteSessionResponse>(
    ctx,
    'DELETE',
    `/api/v1/chat/sessions/${encodeURIComponent(sessionId)}`,
  );
}

/**
 * DELETE /api/v1/chat/sessions — bulk-wipe every saved session. Returns the
 * number of session files the server removed. Mirrors the views bulk-delete
 * endpoint; a missing sessions directory yields `{ deleted: 0 }`.
 */
export async function deleteAllSessions(
  ctx: ApiContext,
): Promise<Result<ChatSessionsDeleteAllResponse, ApiError>> {
  return apiCall<undefined, ChatSessionsDeleteAllResponse>(
    ctx,
    'DELETE',
    `/api/v1/chat/sessions`,
  );
}

/**
 * DELETE /api/v1/chat/sessions/:sessionId/messages — clear all messages
 * but keep the session itself. Server-side counterpart of the
 * `clearMessages` zustand action.
 */
export async function clearMessages(
  ctx: ApiContext,
  sessionId: string,
): Promise<Result<ClearMessagesResponse, ApiError>> {
  return apiCall<undefined, ClearMessagesResponse>(
    ctx,
    'DELETE',
    `/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/messages`,
  );
}

/**
 * DELETE /api/v1/chat/sessions/:sessionId/messages/:messageIndex —
 * splice a single message at `messageIndex` from the session transcript.
 * The index is the 0-based position in the on-disk messages array.
 *
 * `expected` is an optional identity hint (the message's role/content). When
 * supplied, the server treats `messageIndex` as a HINT and verifies + self-
 * heals it against the on-disk transcript, so a client index that has drifted
 * from the server array (multi-message turns persist more messages than the
 * SPA renders as bubbles) never splices out the wrong message. Sent as the
 * request body; a call without it keeps the legacy index-only behaviour.
 */
export async function deleteMessage(
  ctx: ApiContext,
  sessionId: string,
  messageIndex: number,
  expected?: DeleteMessageRequest,
): Promise<Result<DeleteMessageResponse, ApiError>> {
  return apiCall<DeleteMessageRequest | undefined, DeleteMessageResponse>(
    ctx,
    'DELETE',
    `/api/v1/chat/sessions/${encodeURIComponent(sessionId)}/messages/${String(messageIndex)}`,
    expected,
  );
}

export const sessionsApi = {
  listSessions,
  createSession,
  getTranscript,
  deleteSession,
  deleteAllSessions,
  clearMessages,
  deleteMessage,
};

export type SessionsApi = typeof sessionsApi;
